import { Box, Flex, Group, Radio, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { ReactNode } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Frequency } from '../utils';
import SectionCard from './SectionCard';
import { SectionAddButton } from './SectionAddButton';

const ACCORDION_CHEVRON = <IconChevronDown size={20} stroke={1.5} />;

type CashflowSectionProps = {
  value: string;
  title: string;
  count: number;
  totalNode: ReactNode;
  frequency?: Frequency;
  onFrequencyChange?: (frequency: Frequency) => void;
  showFrequencyToggle?: boolean;
  onAdd?: () => void;
  children: ReactNode;
};

export default function CashflowSection({
  value,
  title,
  count,
  totalNode,
  frequency,
  onFrequencyChange,
  showFrequencyToggle = false,
  onAdd,
  children,
}: CashflowSectionProps) {
  const testId = title.toLowerCase().replace(/\s+/g, '-');
  const canToggleFrequency = showFrequencyToggle && frequency && onFrequencyChange;

  return (
    <SectionCard
      value={value}
      title={title}
      count={count}
      variant="cells"
      bg="white"
      chevron={ACCORDION_CHEVRON}
      style={{ borderRadius: 16, overflow: 'hidden' }}
      styles={{ control: { backgroundColor: 'var(--mantine-color-gray-0)' } }}
      controlTestId={`${testId}-control`}
      countTestId={`${testId}-count`}
      panelTestId={`${testId}-panel`}
      rightSection={
        <Group
          gap={24}
          wrap="nowrap"
          align="center"
          pos="absolute"
          top={0}
          bottom={0}
          right={48}
          style={{ pointerEvents: 'none' }}
        >
          <Flex direction="column" align="flex-end" justify="center" gap="xs">
            {canToggleFrequency && (
              <FrequencyToggle
                testId={`${testId}-frequency`}
                frequency={frequency}
                onFrequencyChange={onFrequencyChange}
              />
            )}
            <Box style={{ pointerEvents: 'auto' }}>{totalNode}</Box>
          </Flex>
          {onAdd && (
            <Box style={{ pointerEvents: 'auto' }}>
              <SectionAddButton
                action={{ onAdd }}
                ariaLabel={`Add ${title}`}
                testId={`${testId}-add`}
              />
            </Box>
          )}
        </Group>
      }
    >
      {children}
    </SectionCard>
  );
}

type FrequencyToggleProps = {
  testId: string;
  frequency: Frequency;
  onFrequencyChange: (frequency: Frequency) => void;
};

function FrequencyToggle({ testId, frequency, onFrequencyChange }: FrequencyToggleProps) {
  return (
    <Radio.Group
      value={frequency}
      onChange={value => onFrequencyChange(value as Frequency)}
      data-testid={testId}
      style={{ pointerEvents: 'auto' }}
    >
      <Group gap="md" wrap="nowrap">
        <FrequencyRadio value="monthly" label="Monthly" selected={frequency === 'monthly'} />
        <FrequencyRadio value="annually" label="Annually" selected={frequency === 'annually'} />
      </Group>
    </Radio.Group>
  );
}

type FrequencyRadioProps = {
  value: Frequency;
  label: string;
  selected: boolean;
};

function FrequencyRadio({ value, label, selected }: FrequencyRadioProps) {
  return (
    <Radio
      value={value}
      size="xs"
      color="brand.6"
      label={
        <Text fw={FONT_WEIGHTS.semibold} size="xs" c={selected ? 'brand.7' : 'gray.4'}>
          {label}
        </Text>
      }
      styles={{ radio: { borderColor: selected ? undefined : 'var(--mantine-color-gray-3)' } }}
    />
  );
}
